import React, { useEffect } from 'react';
import { string, func, bool, number, oneOf } from 'prop-types';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';

import Modal from '../Modal/Modal';
import Button, { SecondaryButton } from '../Button/Button';

import css from './ConfirmActionModal.module.css';

const { Money } = sdkTypes;

const VARIANTS = ['chargeNow', 'sendForApproval', 'refund'];

const titleKeyByVariant = {
  chargeNow: 'ConfirmActionModal.chargeNowTitle',
  sendForApproval: 'ConfirmActionModal.sendForApprovalTitle',
  refund: 'ConfirmActionModal.refundTitle',
};

const confirmKeyByVariant = {
  chargeNow: 'ConfirmActionModal.chargeNowConfirm',
  sendForApproval: 'ConfirmActionModal.sendForApprovalConfirm',
  refund: 'ConfirmActionModal.refundConfirm',
};

/**
 * ConfirmActionModal
 *
 * Universal pre-action confirmation modal consumed by 9.5B-4D (add-charge
 * paths), 9.5B-4E (request-refund), and 9.5B-4F (refund-charge, provider
 * refund response, free-form base refund). Three variants share one component
 * to lock copy at the source and eliminate per-consumer drift.
 *
 * The component does NOT close itself after onConfirm. Consumers manage state:
 *   open modal → user confirms → onConfirm fires → consumer sets isPending=true
 *   → async work completes → consumer clears isPending and closes via state.
 *
 * When isPending === true, every dismissal path is blocked:
 *   - Cancel button disabled
 *   - Confirm button disabled with inProgress indicator
 *   - Modal close-button (the X) becomes inert (its onClose is swapped to no-op)
 *   - Escape key becomes inert (handled by the Modal primitive's keyup → onClose)
 *
 * Overlay click is NOT wired by the Modal primitive; per spec, we do not
 * invent it here.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {'chargeNow'|'sendForApproval'|'refund'} props.variant
 * @param {number} props.amountCents — non-negative integer; formatted via formatMoney
 * @param {?string} props.chargeType — required for variant='chargeNow'; ignored otherwise
 * @param {Function} props.onConfirm
 * @param {Function} props.onClose
 * @param {Function} props.onManageDisableScrolling — forwarded to Modal primitive
 * @param {boolean} [props.isPending=false]
 */
const ConfirmActionModal = props => {
  const {
    isOpen,
    variant,
    amountCents,
    chargeType,
    onConfirm,
    onClose,
    onManageDisableScrolling,
    isPending,
  } = props;

  const intl = useIntl();

  // Dev-time warning when chargeNow lacks a chargeType label. Renders the
  // literal `{chargeType}` placeholder downstream so missing consumer input
  // is visible at a glance.
  useEffect(() => {
    if (
      process.env.NODE_ENV !== 'production' &&
      variant === 'chargeNow' &&
      (typeof chargeType !== 'string' || chargeType.trim() === '')
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        '[ConfirmActionModal] variant="chargeNow" requires a non-empty `chargeType` ' +
          'prop. Rendering the literal {chargeType} placeholder so the missing input is visible.'
      );
    }
  }, [variant, chargeType]);

  // Currency is fixed to USD: the marketplace operates in a single currency
  // today and consumers pass raw cents. If multi-currency lands, lift currency
  // derivation to the caller and accept it as a prop.
  const amountMoney = new Money(amountCents, 'USD');
  const formattedAmount = formatMoney(intl, amountMoney);

  const titleValues =
    variant === 'chargeNow'
      ? { amount: formattedAmount, chargeType: chargeType || '{chargeType}' }
      : { amount: formattedAmount };

  // Swap onClose to a no-op while pending so the Modal primitive's Escape
  // handler AND its close-button click both become inert.
  const safeOnClose = isPending ? () => {} : onClose;

  const handleCancel = () => {
    if (isPending) return;
    onClose();
  };

  const handleConfirm = () => {
    if (isPending) return;
    onConfirm();
  };

  return (
    <Modal
      id="ConfirmActionModal"
      isOpen={isOpen}
      onClose={safeOnClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
    >
      <p className={css.title}>
        <FormattedMessage id={titleKeyByVariant[variant]} values={titleValues} />
      </p>
      <div className={css.buttonRow}>
        <SecondaryButton
          type="button"
          className={css.cancelButton}
          onClick={handleCancel}
          disabled={isPending}
        >
          <FormattedMessage id="ConfirmActionModal.cancelButton" />
        </SecondaryButton>
        <Button
          type="button"
          className={css.confirmButton}
          onClick={handleConfirm}
          disabled={isPending}
          inProgress={isPending}
        >
          <FormattedMessage id={confirmKeyByVariant[variant]} />
        </Button>
      </div>
    </Modal>
  );
};

ConfirmActionModal.defaultProps = {
  chargeType: null,
  isPending: false,
};

ConfirmActionModal.propTypes = {
  isOpen: bool.isRequired,
  variant: oneOf(VARIANTS).isRequired,
  amountCents: number.isRequired,
  chargeType: string,
  onConfirm: func.isRequired,
  onClose: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  isPending: bool,
};

export default ConfirmActionModal;
